/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Field, getFormValues } from 'redux-form';
import { mobileUtils } from '@blend/consumer-components';

import { address } from '../../TaskInputs/validators';
import { State } from '../../../../redux';
import { AddressInput } from './types';
import DesktopAddressInputWrapper from './DesktopAddressInputWrapper';
import MobileAddressInputWrapper from './MobileAddressInputWrapper';
import { locale } from './config';
import { get } from '@utils/lodash';
import { getGooglePlaces } from '@components/form/Inputs/AddressInput/utils/googlePlaces';

interface AddressInputProps {
  required: boolean;
  id: string;
  googleApiKey: string;
  display?: string;
}

interface AddressInputMappedProps {
  existingAddresses: AddressInput.Address[];
}

/**
 * This component has been copied over from Borrower (along with the accompanying files in this directory).
 * The AddressInput is a nested Redux form. The parent form includes the AutocompleteDropdown (which uses Google Places API),
 * and the child form is rendered by the modal.
 *
 * Brief summary of how this component works:
 * - The AddressInputWrapper (desktop or mobile) contains the AutocompleteDropdown input and AddressModal
 * - User enters text into the AutocompleteDropdown input which shows results from the Google Places API
 * - User clicks on one of the results from the AutocompleteDropdown, which opens the modal
 *   with all possible fields pre-populated
 * - User can edit any of the individual fields in the modal (the modal contains its own form)
 * - User clicks 'Save', causing the modal to close
 * - The updated address info from the modal is saved as the input value of the parent form
 */

const WITH_COUNTY = false;
const DISPLAY = 'Address'; // used as the header for the modal
const IS_CITY_ONLY = false;
const FORM_NAME = 'task';

type AddressInputCombinedProps = AddressInputProps & AddressInputMappedProps;
class AddressInput extends Component<AddressInputCombinedProps> {
  // TODO (RON-216): Update Address components to be functional
  componentDidMount() {
    const { googleApiKey } = this.props;

    if (!getGooglePlaces()) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = `https://maps.googleapis.com/maps/api/js?v=3.37&libraries=places&key=${googleApiKey}`;
      document.body.appendChild(script);
    }
  }

  render() {
    const { existingAddresses, id, required } = this.props;
    const AddressComponent = mobileUtils.isMobileDevice() ? MobileAddressInputWrapper : DesktopAddressInputWrapper;
    return (
      <Field
        id={id}
        name={id}
        withCounty={WITH_COUNTY}
        existingAddresses={IS_CITY_ONLY ? [] : existingAddresses}
        component={AddressComponent}
        validate={required ? [address] : []}
        locale={locale}
        isCityOnlyInput={IS_CITY_ONLY}
        display={this.props.display || DISPLAY}
      />
    );
  }
}

const mapStateToProps = (state: State, props: AddressInputProps) => {
  const existingAddresses = (() => {
    const formValues = getFormValues(FORM_NAME)(state) as any;
    const currentId = props.id;
    const defaultAddress: AddressInput.Address = {
      city: '',
      countryCode: '',
      line1: '',
      postalCode: '',
      stateCode: '',
    };
    const addressValue = get(formValues, currentId, defaultAddress);
    return [addressValue];
  })();
  return { existingAddresses };
};

export default connect(mapStateToProps)(AddressInput);
